import { useState, useEffect, useCallback } from "react";
import { Divider, Modal, Spin } from "antd";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import HttpStatus from "../../../utility/status.enum";
import { alertModal } from "../../../components/modals/antd-modals";
import { fetchHostListing } from "../../../redux/manageSettings/actionCreator";
import { uploadFile, uploadPdf } from "../../../redux/uploadFile/actionCreator";
import Spinner from "../../common/Spinner";
import Notification from "../../../utility/notification";
import { Button } from "../../../components/buttons/buttons";
import {
    getKnowledeBaseDetails,
    addKnowledgeBaseGroup,
    updateKnowledgeBaseGroup,
    deleteKnowledgeBaseGroup,
    addKnowledgeBaseItem,
    updateKnowledgeBaseItem,
    deleteKnowledgeBaseItem,
} from "../../../redux/knowledgeBase/actionCreator";
import {
    QuestionCard,
    QuestionEditor,
    GroupTitleEditor,
    GroupHeaderRow,
    ListingTable,
} from "./GroupsManagerComponents";

const VISIBILITY_OPTIONS = ["all", "past", "current", "future"];
const VISIBILITY_NUM_TO_LABEL = { 1: "all", 2: "past", 3: "current", 4: "future" };
const VISIBILITY_LABEL_TO_NUM = { all: 1, past: 2, current: 3, future: 4 };

const buildVisibilityPayload = (labels = []) =>
    labels.map((l) => VISIBILITY_LABEL_TO_NUM[l]).filter(Boolean);

const parseVisibility = (raw) => {
    try {
        const parsed = JSON.parse(raw);
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        return arr.map((n) => VISIBILITY_NUM_TO_LABEL[n]).filter(Boolean);
    } catch {
        return [];
    }
};

const DEFAULT_VISIBILITY = [...VISIBILITY_OPTIONS];
const emptyQuestionDraft = { id: "", title: "", description: "", visibility: DEFAULT_VISIBILITY, listingSlugs: [], listing: "" };

// ─── GroupSection ───

const GroupSection = ({ group, contentType, onEditGroup, onDeleteGroup, isDeleting, isEditing, isSavingTitle, onSaveTitle, onCancelTitle, listingLists, showHostComponent, backEndPrefix, dispatch, onRefreshGroups, readOnly = false, allowFileUpload = false, hostSlug = "" }) => {
    const [questions, setQuestions] = useState([]);
    const [editingId, setEditingId] = useState("");
    const [draft, setDraft] = useState(emptyQuestionDraft);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [deletingSlug, setDeletingSlug] = useState("");

    const [sortedListingLists, setSortedListingLists] = useState([]);
    const [tempSelectedListings, setTempSelectedListings] = useState([]);
    const [preSelectedListings, setPreSelectedListings] = useState([]);
    const [isListingPopupOpen, setIsListingPopupOpen] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);

    const openListingViewModal = (slugs = []) => {
        const selectedSlugs = new Set(slugs);
        const selected = [], notSelected = [];
        for (const item of listingLists) {
            (selectedSlugs.has(item.slug) ? selected : notSelected).push(item);
        }
        setSortedListingLists([...selected, ...notSelected]);
        setTempSelectedListings([...slugs]);
        setPreSelectedListings([...slugs]);
        setIsViewMode(true);
        setIsListingPopupOpen(true);
    };

    useEffect(() => {
        const raw = group.questions || [];
        const parseListings = (listings) => {
            if (!listings) return [];
            if (typeof listings === "string") {
                try { const p = JSON.parse(listings); return Array.isArray(p) ? p : []; } catch { return []; }
            }
            return Array.isArray(listings) ? listings : [];
        };
        setQuestions(raw.map((q) => ({
            ...q,
            visibility: Array.isArray(q.content_visibility)
                ? q.content_visibility.map((n) => VISIBILITY_NUM_TO_LABEL[n]).filter(Boolean)
                : parseVisibility(q.content_visibility),
            listingSlugs: parseListings(q.listings).map(String),
            listing: q.listing_name || "",
        })));
    }, [group.questions]);

    const getListingLabel = (slugs = [], fallback = "") => {
        if (!slugs.length || !listingLists.length) return fallback;
        const selected = listingLists.filter((item) => slugs.includes(item.slug));
        if (!selected.length) return fallback;
        if (selected.length === 1) return selected[0]?.listing_name || fallback;
        return (
            <>
                <span>{selected[0]?.listing_name || "Selected listing"}</span>{" "}
                <a className="common_urlLink" href="#" onClick={() => openListingViewModal(slugs)}>
                    & {selected.length - 1} more
                </a>
            </>
        );
    };

    const handleAdd = () => {
        const id = `new-${Date.now()}`;
        setEditingId(id);
        setDraft({ ...emptyQuestionDraft, id, visibility: DEFAULT_VISIBILITY });
    };

    const handleEdit = (question) => {
        setEditingId(question.slug);
        setDraft({
            id: question.slug,
            title: question.title,
            description: question.description || "",
            visibility: question.visibility || [],
            listingSlugs: question.listingSlugs || [],
            listing: question.listing || "",
        });
    };

    const handleDraftChange = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        if (!draft.visibility.length) {
            Notification.addNotificationError("Please select at least one visibility option.");
            return;
        }
        const isNew = draft.id.startsWith("new-");
        const payload = {
            title: draft.title.trim(),
            description: draft.description.trim(),
            content_type: contentType,
            request_type: 1,
            content_visibility: buildVisibilityPayload(draft.visibility),
            group_slug: group.slug,
            listings: [...(draft.listingSlugs || [])],
        };
        if (hostSlug) payload.host_slug = hostSlug;
        setIsSaving(true);
        try {
            const response = isNew
                ? await dispatch(addKnowledgeBaseItem(payload))
                : await dispatch(updateKnowledgeBaseItem(draft.id, payload));
            if (response?.status !== HttpStatus.HTTP_OK) {
                Notification.addNotificationError(response?.message);
                return;
            }
            Notification.addNotificationSuccess(response?.message);
            setEditingId("");
            setDraft(emptyQuestionDraft);
            onRefreshGroups();
        } catch {
            Notification.addNotificationError("An unexpected error occurred.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setEditingId("");
        setDraft(emptyQuestionDraft);
        setTempSelectedListings([]);
        setPreSelectedListings([]);
        setIsListingPopupOpen(false);
    };

    const handleFilePicked = async (e) => {
        const selectedFile = e?.file;
        if (!selectedFile) return;
        setIsUploading(true);
        setDraft((prev) => ({ ...prev, description: "", fileName: "" }));
        try {
            const fileName = selectedFile?.name?.toLowerCase?.() || "";
            const fileType = selectedFile?.type || "";
            const isPdf = fileType === "application/pdf" || fileName.endsWith(".pdf");
            const isTxt = fileType === "text/plain" || fileName.endsWith(".txt");
            const isMd = fileType === "text/markdown" || fileName.endsWith(".md");
            const isImage = fileType.startsWith("image/");
            if (!isPdf && !isTxt && !isMd && !isImage) {
                Notification.addNotificationError("These file types are not allowed.");
                return;
            }
            const formData = new FormData();
            let uploadResponse;
            if (isPdf || isTxt || isMd) {
                formData.append("file_type", 4);
                formData.append("file", selectedFile);
                uploadResponse = await dispatch(uploadPdf(formData));
            } else {
                formData.append("imageType", 8);
                formData.append("file", selectedFile);
                uploadResponse = await dispatch(uploadFile(formData));
            }
            if (uploadResponse?.status === HttpStatus.HTTP_OK) {
                const { fileName: uploadedName, filePath } = uploadResponse.result;
                setDraft((prev) => ({ ...prev, fileName: uploadedName, description: filePath }));
            } else {
                Notification.addNotificationError(uploadResponse?.message);
            }
        } catch {
            Notification.addNotificationError("File upload failed.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileRemove = () => setDraft((prev) => ({ ...prev, fileName: "", description: "" }));

    const handleDeleteQuestion = (question) => {
        alertModal.confirm({
            title: "Do you want to delete this item?",
            className: "confirm_modal",
            onOk() {
                const deleteQuestion = async () => {
                    setDeletingSlug(question.slug);
                    try {
                        const response = await dispatch(deleteKnowledgeBaseItem(question.slug));
                        if (response?.status !== HttpStatus.HTTP_OK) {
                            Notification.addNotificationError(response?.message);
                            return;
                        }
                        Notification.addNotificationSuccess(response?.message);
                        onRefreshGroups();
                    } catch {
                        Notification.addNotificationError("An unexpected error occurred.");
                    } finally {
                        setDeletingSlug("");
                    }
                };
                deleteQuestion();
            },
            onCancel() {},
        });
    };

    const openListingPopup = () => {
        const selectedSlugs = new Set(draft.listingSlugs || []);
        const selected = [], notSelected = [];
        for (const item of listingLists) {
            (selectedSlugs.has(item.slug) ? selected : notSelected).push(item);
        }
        setSortedListingLists([...selected, ...notSelected]);
        setPreSelectedListings(structuredClone(draft.listingSlugs || []));
        setTempSelectedListings(structuredClone(draft.listingSlugs || []));
        setIsListingPopupOpen(true);
        setIsViewMode(false);
    };

    const closeListingPopup = (cancelled = false) => {
        setIsListingPopupOpen(false);
        if (cancelled) setTempSelectedListings(preSelectedListings || []);
    };

    const handleListingCheckboxChange = (event, slug) => {
        const { checked } = event.target;
        setTempSelectedListings((prev) =>
            checked ? [...new Set([...prev, slug])] : prev.filter((s) => s !== slug)
        );
    };

    const handleListingSelection = () => {
        const label = getListingLabel(tempSelectedListings, draft.listing);
        setDraft((prev) => ({ ...prev, listingSlugs: [...tempSelectedListings], listing: label }));
        closeListingPopup();
    };

    const editorProps = {
        draft, isSaving, isUploading,
        onChange: handleDraftChange,
        onVisibility: (val) => handleDraftChange("visibility", val),
        onOpenListingPopup: openListingPopup,
        onSave: handleSave,
        onCancel: handleCancel,
        onFilePicked: handleFilePicked,
        onFileRemove: handleFileRemove,
        showHostComponent,
        allowFileUpload,
    };

    return (
        <Spin spinning={isDeleting}>
            <div>
                {isEditing ? (
                    <GroupTitleEditor initialTitle={group.title} isSaving={isSavingTitle} onSave={onSaveTitle} onCancel={onCancelTitle} />
                ) : (
                    <GroupHeaderRow group={group} onEditGroup={onEditGroup} onDeleteGroup={onDeleteGroup} readOnly={readOnly} />
                )}
                <div className="ai-kb-group-questions">
                    <Spinner loader={isSaving} />
                    {questions.map((q) => {
                        if (!readOnly && editingId === q.slug) return <QuestionEditor key={q.slug} {...editorProps} />;
                        return (
                            <QuestionCard
                                key={q.slug}
                                question={q}
                                listingLabel={getListingLabel(q.listingSlugs, q.listing)}
                                onEdit={handleEdit}
                                onDelete={handleDeleteQuestion}
                                isDeleting={deletingSlug === q.slug}
                                readOnly={readOnly}
                            />
                        );
                    })}
                    {editingId && editingId.startsWith("new-") && <QuestionEditor {...editorProps} />}
                    {!readOnly && !editingId && (
                        <button type="button" className="ai-kb-add-question-btn" onClick={handleAdd}>
                            + Add Question
                        </button>
                    )}
                </div>
                <Divider className="mt-20 mb-19" />
            </div>

            <Modal
                type="primary"
                open={isListingPopupOpen}
                footer={null}
                onCancel={() => closeListingPopup(true)}
                width="90%"
                maskClosable={false}
                aria-hidden="false"
                className="bulk-listing-modal template-config-listing-modal"
                title={`Selected Listings (${tempSelectedListings.length}/${listingLists?.length || 0})`}
            >
                <div className="guide-matching-listing">
                    <div className="project-modal">
                        <div className="guide-card">
                            <div className="guide-card-body">
                                <ListingTable
                                    sortedListingLists={sortedListingLists}
                                    tempSelectedListings={tempSelectedListings}
                                    onCheckboxChange={handleListingCheckboxChange}
                                    backEndPrefix={backEndPrefix}
                                />
                            </div>
                            {!isViewMode && (
                                <div className="bulk-editbtn-box">
                                    <Button htmlType="submit" size="default" type="dark" shape="circle" key="submit" onClick={handleListingSelection}>
                                        Update
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </Spin>
    );
};

// ─── KBGroupManager ────

const KBGroupManager = ({ contentType, readOnly = false, allowFileUpload = false, isAiChatEnabled = true, isKbEnabled = true }) => {
    const { backEndPrefix, hostLoginRoleID } = global;
    const dispatch = useDispatch();
    const hostSlug = useParams()?.hostSlug ?? "";

    const [groups, setGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [addingNew, setAddingNew] = useState(false);
    const [isSavingGroup, setIsSavingGroup] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [deletingSlug, setDeletingSlug] = useState("");
    const [listingLists, setListingLists] = useState([]);
    const [showHostComponent, setShowHostComponent] = useState(false);
    const [isListingsLoading, setIsListingsLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const payload = { request_type: 1, content_type: contentType };
            if (hostSlug) payload.host_slug = hostSlug;
            const response = await dispatch(getKnowledeBaseDetails(payload));
            if (response?.status === HttpStatus.HTTP_OK) {
                setGroups(Array.isArray(response?.result) ? response.result : []);
            } else if (response) {
                Notification.addNotificationError(response?.message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, contentType]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return;
        if (user.role_id === hostLoginRoleID) {
            setShowHostComponent(true);
            setIsListingsLoading(true);
            dispatch(fetchHostListing({ host_slug: hostSlug }))
                .then((res) => {
                    if (res?.status === HttpStatus.HTTP_OK) setListingLists(res?.result || []);
                })
                .finally(() => setIsListingsLoading(false));
        }
    }, []);

    const handleAddGroupSave = async (title) => {
        setIsSavingGroup(true);
        try {
            const payload = { title, content_type: contentType };
            if (hostSlug) payload.host_slug = hostSlug;
            const response = await dispatch(addKnowledgeBaseGroup(payload));
            if (response?.status !== HttpStatus.HTTP_OK) {
                Notification.addNotificationError(response?.message);
                return;
            }
            Notification.addNotificationSuccess(response?.message);
            setAddingNew(false);
            fetchData();
        } finally {
            setIsSavingGroup(false);
        }
    };

    const handleEditGroupSave = async (title) => {
        if (!editingGroup) return;
        setIsSavingGroup(true);
        try {
            const payload = { title };
            if (hostSlug) payload.host_slug = hostSlug;
            const response = await dispatch(updateKnowledgeBaseGroup(editingGroup.slug, payload));
            if (response?.status !== HttpStatus.HTTP_OK) {
                Notification.addNotificationError(response?.message);
                return;
            }
            Notification.addNotificationSuccess(response?.message);
            setEditingGroup(null);
            fetchData();
        } finally {
            setIsSavingGroup(false);
        }
    };

    const handleDeleteGroup = (group) => {
        alertModal.confirm({
            title: "Do you want to delete this group?",
            className: "confirm_modal",
            onOk() {
                const deleteGroup = async () => {
                    setDeletingSlug(group.slug);
                    try {
                        const response = await dispatch(deleteKnowledgeBaseGroup(group.slug));
                        if (response?.status !== HttpStatus.HTTP_OK) {
                            Notification.addNotificationError(response?.message);
                            return;
                        }
                        Notification.addNotificationSuccess(response?.message);
                        fetchData();
                    } finally {
                        setDeletingSlug("");
                    }
                };
                deleteGroup();
            },
            onCancel() {},
        });
    };

    const canAddGroup = !readOnly && isAiChatEnabled && isKbEnabled;

    return (
        <div className="ai-kb-resources-manager">
            {/* Add Group button */}
            {canAddGroup && (
                <div className="ai-kb-resource-header mb-10">
                    <div className="ai-kb-resource-header-copy" />
                    <button
                        type="button"
                        className="ai-kb-add-btn"
                        onClick={() => { setAddingNew(true); setEditingGroup(null); }}
                        disabled={addingNew}
                    >
                        + Add Group
                    </button>
                </div>
            )}

            <Spinner loader={isLoading || isSavingGroup || isListingsLoading} />

            <div className="ai-kb-resources-list">
                {canAddGroup && addingNew && (
                    <GroupTitleEditor
                        initialTitle=""
                        isSaving={isSavingGroup}
                        onSave={handleAddGroupSave}
                        onCancel={() => setAddingNew(false)}
                    />
                )}
                {groups.map((group) => {
                    if (editingGroup?.slug === group.slug) {
                        return (
                            <GroupTitleEditor
                                key={group.slug}
                                initialTitle={group.title}
                                isSaving={isSavingGroup}
                                onSave={handleEditGroupSave}
                                onCancel={() => setEditingGroup(null)}
                            />
                        );
                    }
                    return (
                        <GroupSection
                            key={group.slug}
                            group={group}
                            contentType={contentType}
                            onEditGroup={(g) => { setEditingGroup(g); setAddingNew(false); }}
                            onDeleteGroup={handleDeleteGroup}
                            isDeleting={deletingSlug === group.slug}
                            listingLists={listingLists}
                            showHostComponent={showHostComponent}
                            backEndPrefix={backEndPrefix}
                            dispatch={dispatch}
                            onRefreshGroups={fetchData}
                            readOnly={readOnly}
                            allowFileUpload={allowFileUpload}
                            hostSlug={hostSlug}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default KBGroupManager;
